import { CheckCircleFilled } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { Form } from "components/Form/Form"
import { Input } from "components/Input/Input"
import { Space } from "components/Space/Space"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { SerialNumberScanner } from "components/SerialNumberScanner/SerialNumberScanner"
import { Visit } from "interface/visit"
import { capitalize } from "lodash-es"
import { useEffect, useState } from "react"
import { usePatientDevice } from "services/patients"
import { useRegisterDevice, useUnregisterDevice } from "services/visits"
import { cn } from "styles/cn"
import { getOS } from "utils/os"
import { useStepCompletion } from "./useStepCompletion"

export function StepLinkFootprint({ visit }: { visit: Visit }) {
	const [serialNumber, setSerialNumber] = useState<string>("")
	const { data: patientDevice, isFetching } = usePatientDevice(
		visit.patient?.id!,
	)
	const register = useRegisterDevice(visit.id)
	const unregister = useUnregisterDevice(visit.id)
	const { markStepAs, linkFootprint } = useStepCompletion(visit)
	const isFootprintLinked = !!patientDevice?.deviceId
	const linkingError = register.isError && !register.isPending
	const isVisitComplete = visit.status === "COMPLETED"
	const { isIOS } = getOS()
	const iOSWarningMessage = isIOS
		? "Scanning serial numbers with iPhone is not supported. Please, input the serial number."
		: undefined
	const placeholder = capitalize(
		`${isIOS ? "" : "Scan or "}input the serial number`,
	)

	useEffect(() => {
		if (isFootprintLinked && !linkFootprint && !isVisitComplete) {
			markStepAs("linkFootprint")
		}
	}, [isFootprintLinked, isVisitComplete, linkFootprint, markStepAs])

	return (
		<div className="flex size-full grow flex-col items-center justify-center gap-3 text-center">
			{isFootprintLinked && (
				<>
					<CheckCircleFilled
						aria-hidden
						className={cn(
							"text-[48px] text-green-60",
							!isVisitComplete && "mt-auto",
						)}
					/>
					The Footprint is connected to the patient's account
					<ErrorStatus
						className="mt-auto"
						show={unregister.isError && !unregister.isPending}
					>
						{unregister.error?.message}
					</ErrorStatus>
					{!isVisitComplete && (
						<Button
							type="primary"
							className="mt-auto w-full"
							disabled={isFetching}
							loading={unregister.isPending}
							onClick={() =>
								unregister.mutate(patientDevice?.deviceId!, {
									onSuccess: () => {
										markStepAs("linkFootprint", false)
									},
								})
							}
						>
							Unlink Footprint
						</Button>
					)}
				</>
			)}

			{!isFootprintLinked && (
				<>
					Link the patient's account to their Footprint
					<div className="flex w-full flex-col items-center">
						<Form.Item
							hasFeedback={linkingError}
							validateStatus={linkingError ? "error" : undefined}
							extra={iOSWarningMessage}
						>
							<Space.Compact>
								<Input
									allowClear
									placeholder={placeholder}
									autoComplete="off"
									value={serialNumber}
									disabled={register.isPending || isFetching}
									onChange={({ target }) => setSerialNumber(target.value)}
									classNames={{ suffix: "cursor-auto" }}
								/>
								<SerialNumberScanner
									onCapture={setSerialNumber}
									loading={register.isPending}
								/>
							</Space.Compact>
						</Form.Item>
						<ErrorStatus
							className="mt-auto"
							show={register.isError && !register.isPending}
						>
							{register.error?.message}
						</ErrorStatus>
					</div>
					<Button
						type="primary"
						className="mt-auto w-full"
						disabled={serialNumber.length === 0 || isFetching}
						loading={register.isPending}
						onClick={() => register.mutate(serialNumber)}
					>
						Link Footprint
					</Button>
				</>
			)}
		</div>
	)
}
